import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { ApiRoutes } from '@/constants';
import { setAccessTokenToHeaders } from '@/helpers';
import { envUtil } from '@/utils';

import type {
  IRequestParams,
  IResponse,
  IUserResponse,
  IUserFormParams,
} from '@/types';

const { api } = envUtil.getEnv();

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: api,
    prepareHeaders: (headers) => setAccessTokenToHeaders(headers),
  }),
  tagTypes: ['Users', 'User'],
  endpoints: (builder) => ({
    getUsers: builder.query<IResponse<IUserResponse>, IRequestParams>({
      query: (params) => ({
        url: ApiRoutes.Users,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: 'Users' as const,
                id,
              })),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
      keepUnusedDataFor: 1,
    }),
    getUserById: builder.query<IUserResponse, string>({
      query: (id) => ({
        url: `${ApiRoutes.Users}/${id}`,
      }),
      providesTags: () => ['User'],
    }),
    editUser: builder.mutation<IUserResponse, IUserFormParams & { id: string }>(
      {
        query: ({ id, ...body }) => ({
          url: `${ApiRoutes.Users}/${id}`,
          body,
          method: 'PUT',
        }),
        invalidatesTags: ['User'],
      }
    ),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ApiRoutes.Users}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    createUser: builder.mutation<IUserResponse, IUserFormParams>({
      query: (body) => ({
        url: `${ApiRoutes.Users}`,
        body,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useEditUserMutation,
  useCreateUserMutation,
} = usersApi;
